import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, type WorkOrder } from '../lib/api'
import { money, currentPeriod } from '../lib/format'
import { Panel, Alert, Loading, Empty, Tag } from '../components/ui'
import MonthRangePicker from '../components/MonthRangePicker'

export default function WorkOrders() {
  const nav = useNavigate()
  const [from, setFrom] = useState(currentPeriod())
  const [to, setTo] = useState(currentPeriod())
  /** 跨月才需要顯示「月份」欄，單月時每列都一樣沒意義 */
  const multiMonth = from !== to
  const [status, setStatus] = useState('')
  /* 篩選在前端做，跟驗收單列表同一套：資料已經抓回來了，一個區間頂多數十列。
     text＝輸入框當下的字，q＝已套用的關鍵字；按「搜尋」或 Enter 才套用 */
  const [text, setText] = useState('')
  const [q, setQ] = useState('')
  const [rows, setRows] = useState<WorkOrder[] | null>(null)
  const [error, setError] = useState('')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [msg, setMsg] = useState('')
  /** 正在改狀態的那一列，避免連點送兩次 */
  const [changing, setChanging] = useState<number | null>(null)

  const load = () => {
    setRows(null)
    setSelected(new Set())
    const qs = new URLSearchParams({ from, to })
    if (status) qs.set('status', status)
    api.get<WorkOrder[]>(`/work-orders?${qs}`).then(setRows).catch((e) => setError(e.message))
  }

  useEffect(load, [from, to, status])

  const shown = useMemo(() => {
    const kw = q.trim().toLowerCase()
    if (!kw) return rows ?? []
    // 一個搜尋框涵蓋發包單號、承包人、客戶，不必先選對下拉才找得到
    return (rows ?? []).filter((w) =>
      [w.code, w.writer_name, w.writer_name_en, w.client_names]
        .some((v) => String(v ?? '').toLowerCase().includes(kw))
    )
  }, [rows, q])

  const filtering = Boolean(q.trim())
  const rangeLabel = multiMonth ? `${from} ～ ${to}` : '這個月'

  /**
   * 清單上直接改狀態。
   *
   * 「待補充」只在本來就是待補充時才留在選項裡 —— 送出後要退回草稿是管理員的
   * 「解鎖編輯」，那條路在發包單頁，會連同欄位一起解鎖，不該從清單偷改。
   * 「待審核」也不列：那是按「送出審核」走的流程，不是手動選的狀態。
   */
  const STATUS_OPTIONS = ['待確認', '待交稿', '已交稿', '驗收中', '已完成', '終止']

  const changeStatus = async (w: WorkOrder, status: string) => {
    if (status === w.status) return
    setError('')
    setChanging(w.id)
    try {
      const res = await api.post<WorkOrder>(`/work-orders/${w.id}/status`, { status })
      // 只換掉這一列，不整份重載 —— 免得勾選狀態與捲動位置被洗掉
      setRows((list) => (list ?? []).map((x) => (x.id === w.id ? { ...x, status: res.status } : x)))
    } catch (e: any) {
      setError(e.message)
    } finally {
      setChanging(null)
    }
  }

  const allSelected = shown.length > 0 && shown.every((w) => selected.has(w.id))

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(shown.map((w) => w.id)))
  }

  const printSelected = () => {
    if (selected.size === 0) return
    nav(`/work-orders/print-batch?ids=${[...selected].join(',')}`)
  }

  const picked = shown.filter((w) => selected.has(w.id))

  /* -------- 合併：同承包人、同月份、皆為「待補充」且無驗收紀錄 -------- */
  const mergeable =
    picked.length >= 2 &&
    new Set(picked.map((w) => w.writer_id)).size === 1 &&
    new Set(picked.map((w) => w.period)).size === 1 &&
    picked.every((w) => w.status === '待補充' && !w.acceptance_count)

  const mergeHint = () => {
    if (picked.length < 2) return '請勾選 2 張以上的發包單'
    if (new Set(picked.map((w) => w.writer_id)).size > 1) return '承包人不同，不可合併'
    if (new Set(picked.map((w) => w.period)).size > 1) return '月份不同，不可合併'
    if (picked.some((w) => w.acceptance_count)) return '已有驗收紀錄的發包單不可合併'
    if (picked.some((w) => w.status !== '待補充')) return '僅「待補充」的發包單可以合併'
    return undefined
  }

  const mergeSelected = async () => {
    if (!mergeable) return
    // 後端保留單號最小者，這裡照同樣規則算出來顯示，避免使用者以為留的是別張
    const sorted = [...picked].sort((a, b) => a.code.localeCompare(b.code))
    const keep = sorted[0]
    const drop = sorted.slice(1)
    const ok = confirm(
      `確定將 ${sorted.map((w) => w.code).join('、')} 合併為一張？\n\n` +
      `保留 ${keep.code}，共 ${sorted.reduce((n, w) => n + w.total_qty, 0)} 篇 / ` +
      `${money(sorted.reduce((n, w) => n + w.total_amount, 0))} 元。\n` +
      `${drop.map((w) => w.code).join('、')} 會先收進 ${keep.code}。\n\n` +
      `之後隨時可以按「取消合併」把它們變回原本的單（單號會一起還原）。`
    )
    if (!ok) return
    setError(''); setMsg('')
    try {
      const res = await api.post<{ code: string; merged_codes: string[] }>(
        '/work-orders/merge',
        { ids: sorted.map((w) => w.id) }
      )
      setMsg(`已將 ${res.merged_codes.join('、')} 合併進 ${res.code}`)
      load()
    } catch (e: any) {
      setError(e.message)
    }
  }

  /* -------- 取消合併：按鈕只出現在合併過的那一列 -------- */
  const unmerge = async (w: WorkOrder) => {
    const ok = confirm(
      `確定要取消 ${w.code} 的合併？\n\n` +
      `其中 ${w.merged_item_count} 列合併進來的明細會搬回原本的發包單，原單號一併還原。\n` +
      `合併後才新增的明細會留在 ${w.code}。\n\n` +
      `此動作無法復原（但你可以再合併一次）。`
    )
    if (!ok) return
    setError(''); setMsg('')
    try {
      const res = await api.post<{ code: string; restored: string[]; missing: string[] }>(
        `/work-orders/${w.id}/unmerge`
      )
      setMsg(
        `已從 ${res.code} 還原 ${res.restored.join('、')}` +
        (res.missing.length ? `（${res.missing.join('、')} 的明細已被刪除，未還原）` : '')
      )
      load()
    } catch (e: any) {
      setError(e.message)
    }
  }

  const revertToEvaluation = async (w: WorkOrder) => {
    const ok = confirm(
      `確定將發包單 ${w.code} 退回評估？\n\n` +
      `此發包單會被刪除，來源的 ${w.evaluation_count} 筆評估紀錄會回到「評估中」，` +
      `你可以到「每月發包評估」重新編輯或再次轉單。\n\n此動作無法復原。`
    )
    if (!ok) return
    setError(''); setMsg('')
    try {
      await api.del(`/work-orders/${w.id}`)
      setMsg(`已將 ${w.code} 退回評估，來源評估紀錄已回到「評估中」`)
      load()
    } catch (e: any) {
      setError(e.message)
    }
  }

  const remove = async (w: WorkOrder) => {
    const evalNote = w.evaluation_count
      ? `\n\n來源的 ${w.evaluation_count} 筆評估紀錄會一併回到「評估中」（不會被刪除）。`
      : ''
    const ok = confirm(`確定刪除發包單 ${w.code}？${evalNote}\n\n此動作無法復原。`)
    if (!ok) return
    setError(''); setMsg('')
    try {
      await api.del(`/work-orders/${w.id}`)
      setMsg(`已刪除 ${w.code}`)
      load()
    } catch (e: any) {
      setError(e.message)
    }
  }

  return (
    <>
      <div className="page-head">
        <h2>發包單列表</h2>
        <span className="hint">月份</span>
        <MonthRangePicker from={from} to={to} onChange={(a, b) => { setFrom(a); setTo(b) }} />
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 120 }}>
          <option value="">全部狀態</option>
          {['待補充', '待審核', '待確認', '待交稿', '已交稿', '驗收中', '已完成', '終止'].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div className="spacer" />
        <div className="btn-row">
          <span className="hint" style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
            已勾選 {selected.size} / {shown.length} 張
          </span>
          <button onClick={toggleAll} disabled={shown.length === 0}>
            {allSelected ? '取消全選' : '全選'}
          </button>
          <button disabled={selected.size === 0} onClick={printSelected}>
            批次列印（{selected.size}）
          </button>
          <button disabled={!mergeable} title={mergeHint()} onClick={mergeSelected}>
            合併發包單（{selected.size}）
          </button>
        </div>
      </div>

      <div className="btn-row" style={{ marginBottom: 12 }}>
        <input
          style={{ width: 280 }}
          value={text}
          onChange={(e) => setText(e.target.value)}
          // 打完直接按 Enter 就搜，不必移到滑鼠
          onKeyDown={(e) => { if (e.key === 'Enter') setQ(text) }}
          placeholder="搜尋發包單號／承包人／客戶"
        />
        <button className="primary" onClick={() => setQ(text)}>搜尋</button>
        {filtering && (
          <button onClick={() => { setText(''); setQ('') }}>
            清除篩選
          </button>
        )}
      </div>

      <Alert kind="error">{error}</Alert>
      {msg && <div className="alert ok">{msg}</div>}

      <Panel>
        {!rows ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>{rangeLabel}還沒有發包單</Empty>
        ) : shown.length === 0 ? (
          // 跟「這個區間沒資料」分開講，否則使用者會以為資料不見了
          <Empty>{rangeLabel}共 {rows.length} 張發包單，但沒有符合篩選條件的</Empty>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  {multiMonth && <th style={{ width: 74 }}>月份</th>}
                  <th>發包單號</th>
                  <th style={{ width: 56 }}>類型</th>
                  <th>承包人</th>
                  <th>負責人</th>
                  <th>發包日</th>
                  <th>約定交件日</th>
                  <th className="num">篇數</th>
                  <th className="num">金額</th>
                  <th>狀態</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((w) => (
                  <tr key={w.id} style={selected.has(w.id) ? { background: 'var(--accent-soft)' } : undefined}>
                    <td>
                      <input
                        type="checkbox"
                        style={{ width: 'auto' }}
                        checked={selected.has(w.id)}
                        onChange={() => toggleOne(w.id)}
                      />
                    </td>
                    {multiMonth && <td>{w.period}</td>}
                    <td>
                      <Link to={`/work-orders/${w.id}`}>{w.code}</Link>
                      {!!w.merged_item_count && (
                        <span
                          className="hint"
                          style={{ marginLeft: 6, fontSize: 11, color: 'var(--ink-soft)' }}
                          title={`含 ${w.merged_item_count} 列由其他發包單合併進來的明細，可以取消合併`}
                        >
                          合併
                        </span>
                      )}
                    </td>
                    <td>
                      {/* 內包才標色；外包是常態，跟著上色反而看不出重點 */}
                      {w.dispatch_type === '內包'
                        ? <span className="tag info">內包</span>
                        : <span style={{ color: 'var(--ink-soft)' }}>外包</span>}
                    </td>
                    <td>{w.writer_name} {w.writer_name_en}</td>
                    <td>{w.owner_name}</td>
                    <td>{w.issued_on}</td>
                    <td>{w.due_on}</td>
                    <td className="num">{w.total_qty}</td>
                    <td className="num">{money(w.total_amount)}</td>
                    <td>
                      {/* 待補充／待審核是流程關卡，只顯示不給改；其餘狀態可以直接在清單上調 */}
                      {w.status === '待補充' || w.status === '待審核' ? (
                        <Tag value={w.status} />
                      ) : (
                        <select
                          value={w.status}
                          disabled={changing === w.id}
                          style={{ width: 92 }}
                          onChange={(e) => changeStatus(w, e.target.value)}
                        >
                          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      )}
                    </td>
                    <td>
                      <div className="link-actions">
                        <Link to={`/work-orders/${w.id}/print`}>列印</Link>
                        {/* 只有合併過的單才看得到這顆按鈕 */}
                        {!!w.merged_item_count && (
                          <button
                            disabled={w.status !== '待補充' || !!w.acceptance_count}
                            title={
                              w.acceptance_count
                                ? '已有驗收紀錄，不可取消合併'
                                : w.status !== '待補充'
                                ? '僅「待補充」的發包單可以取消合併'
                                : `把 ${w.merged_item_count} 列合併進來的明細搬回原本的發包單`
                            }
                            onClick={() => unmerge(w)}
                          >
                            取消合併
                          </button>
                        )}
                        <button
                          disabled={!w.evaluation_count || !!w.acceptance_count}
                          title={
                            w.acceptance_count
                              ? '已有驗收紀錄，不可退回評估'
                              : !w.evaluation_count
                              ? '此發包單並非由評估表轉入'
                              : undefined
                          }
                          onClick={() => revertToEvaluation(w)}
                        >
                          退回評估
                        </button>
                        <button
                          className="danger"
                          disabled={!!w.acceptance_count}
                          title={w.acceptance_count ? '已有驗收紀錄，不可刪除' : undefined}
                          onClick={() => remove(w)}
                        >
                          刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  {/* 勾選＋（月份）＋單號＋類型＋承包人＋負責人＋發包日＋交件日，跨月時多一欄 */}
                  <td colSpan={multiMonth ? 8 : 7}>
                    合計 {shown.length} 張
                    {filtering && `（已篩選，區間內共 ${rows.length} 張）`}
                  </td>
                  <td className="num">{shown.reduce((n, w) => n + w.total_qty, 0)}</td>
                  <td className="num">{money(shown.reduce((n, w) => n + w.total_amount, 0))}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Panel>
    </>
  )
}
